/* eslint-disable no-plusplus */
/* eslint-disable no-restricted-syntax */
import Moment from 'moment';
import { extendMoment } from 'moment-range';
import { DriveInfo, IBookingDrive, IFormDriveEdit, MappedDrive } from '@common/interfaces';
import { IDropdownOption } from '@ui-modules/types';

const moment = extendMoment(Moment as any);

const TIME_INTERVAL = 15;

type IValidator = (value: unknown) => string | void;

type IValidatorExt = (value: unknown, values: Record<string, unknown>) => unknown | void;

interface ICalculateSharedDrive {
  drives: MappedDrive[];
  dropoff_date: Date;
  dropoff_time: Date;
  pickup_date: Date;
  pickup_time: Date;
  vehicle: IDropdownOption;
  driver?: IDropdownOption;
  bookingId?: string;
  isMultileg?: boolean;
}

export const getSoonTime = (date: Date): Date => {
  const roundedMinutes = Math.round(moment(date).clone().minute() / TIME_INTERVAL) * TIME_INTERVAL;
  return moment(date).clone().minute(roundedMinutes).second(0).toDate();
};

export const getFullDate = (date: Date, time: Date): Date =>
  moment(
    `${moment(date).format('YYYY-MM-DD')} ${moment(time).format('HH:mm')}`,
    'YYYY-MM-DD HH:mm',
  ).toDate();

export const validateWithUser =
  (validator: IValidator): IValidatorExt =>
  (value: unknown, { main_info: { no_passengers } }: Record<string, any>) => {
    if (!no_passengers) {
      return validator(value);
    }
  };

/* We get start dates, end dates and vehicle so we should calculate do we have some drive
 * in these dates with this vehicle. Filtrate the array of all drives and:
 * - return null, if we have no results
 * - return first result if we have some results
 * We haven't logic for multi-results for now, so just use first result
 */
export const calculateSharedDrive = ({
  drives,
  dropoff_date,
  dropoff_time,
  pickup_date,
  pickup_time,
  vehicle,
  driver,
}: ICalculateSharedDrive): null | MappedDrive => {
  const sharedDrive = drives
    .filter((drive: MappedDrive) => {
      if (!driver) {
        return drive.vehicle_id === vehicle?.value;
      }
      // If driver passed, select also drives that have the same driver
      return drive.vehicle_id === vehicle?.value || drive.driver_id === driver?.value;
    })
    .filter((drive: MappedDrive) => {
      const existDriveRange = moment.range(drive.start_date, drive.end_date);
      const selectedDriveRange = moment.range(
        getFullDate(pickup_date, pickup_time),
        getFullDate(dropoff_date, dropoff_time),
      );

      return existDriveRange.overlaps(selectedDriveRange);
    });

  if (sharedDrive.length !== 0) {
    return sharedDrive[0];
  }

  return null;
};

const checkOverlap = (firstDrive: DriveInfo, secondDrive: DriveInfo): boolean => {
  if (
    firstDrive.driver.value !== secondDrive.driver.value &&
    firstDrive.vehicle.value !== secondDrive.vehicle.value
  ) {
    return false;
  }

  const firstDriveRange = moment.range(
    getFullDate(firstDrive.pickup_date, firstDrive.pickup_time),
    getFullDate(firstDrive.dropoff_date, firstDrive.dropoff_time),
  );

  const secondDriveRange = moment.range(
    getFullDate(secondDrive.pickup_date, secondDrive.pickup_time),
    getFullDate(secondDrive.dropoff_date, secondDrive.dropoff_time),
  );

  return firstDriveRange.overlaps(secondDriveRange);
};

export const checkConflictingDrivesOnPlanner = ({
  drives,
  pickup_date,
  pickup_time,
  dropoff_date,
  dropoff_time,
  driver,
  vehicle,
}: ICalculateSharedDrive): boolean => {
  const overlappingDrives = drives.filter((drive: MappedDrive) => {
    const existDriveRange = moment.range(drive.start_date, drive.end_date);
    const selectedDriveRange = moment.range(
      getFullDate(pickup_date, pickup_time),
      getFullDate(dropoff_date, dropoff_time),
    );

    return existDriveRange.overlaps(selectedDriveRange) && drive.vehicle_id !== vehicle.value;
  });

  for (const drive of overlappingDrives) {
    if (driver?.value && drive.driver_id === driver.value) {
      return true;
    }
  }

  return false;
};

export const checkConflictingDrivesOnAcceptance = ({
  drives,
  pickup_date,
  pickup_time,
  dropoff_date,
  dropoff_time,
  driver,
  vehicle,
  bookingId,
}: ICalculateSharedDrive): { conflict: 'driver' | 'vehicle' | null } => {
  const relevantDrives = drives.filter(
    (drive) =>
      !drive.is_temporary &&
      drive.booking !== bookingId &&
      (drive.vehicle_id === vehicle.value || drive.driver_id === driver?.value),
  );

  const selectedDriveRange = moment.range(
    getFullDate(pickup_date, pickup_time),
    getFullDate(dropoff_date, dropoff_time),
  );

  for (const drive of relevantDrives) {
    const driveRange = moment.range(drive.start_date, drive.end_date);

    if (
      selectedDriveRange.intersect(driveRange) &&
      !(drive.vehicle_id === vehicle.value && drive.driver_id === driver?.value)
    ) {
      return { conflict: drive.vehicle_id === vehicle.value ? 'vehicle' : 'driver' };
    }
  }

  return { conflict: null };
};

export const checkDriveIsRideShared = ({
  driver,
  drives,
  dropoff_date,
  dropoff_time,
  pickup_date,
  pickup_time,
  vehicle,
}: {
  driver?: string;
  drives: MappedDrive[];
  dropoff_date: Date;
  dropoff_time: Date;
  pickup_date: Date;
  pickup_time: Date;
  vehicle: string;
}): boolean => {
  const relevantDrives = drives.filter(
    (drive) => !drive.is_temporary && (drive.vehicle_id === vehicle || drive.driver_id === driver),
  );

  const selectedDriveRange = moment.range(
    moment(`${pickup_date} ${pickup_time}`, 'YYYY-MM-DD HH:mm'),
    moment(`${dropoff_date} ${dropoff_time}`, 'YYYY-MM-DD HH:mm'),
  );

  for (const drive of relevantDrives) {
    const driveRange = moment.range(drive.start_date, drive.end_date);

    if (
      selectedDriveRange.intersect(driveRange) &&
      drive.vehicle_id === vehicle &&
      drive.driver_id === driver
    ) {
      return true;
    }
  }

  return false;
};

export const checkOverlappingDrives = (drives: DriveInfo[]): boolean => {
  if (drives?.length <= 1) {
    return false;
  }

  const checkedIndexes: string[] = [];
  for (let i = 0; i <= drives.length; i++) {
    for (let j = i + 1; j < drives.length; j++) {
      if (!checkedIndexes.includes(`${i}${j}`)) {
        const isOverlapping = checkOverlap(drives[i], drives[j]);

        if (isOverlapping) {
          return true;
        }

        checkedIndexes.push(`${i}${j}`);
      }
    }
  }

  return false;
};

// New Mobility Planner ==========================================================
interface DriveProps {
  drives: IBookingDrive[];
  driver: IDropdownOption;
  dropoffDate: Date;
  dropoffTime: Date;
  pickupDate: Date;
  pickupTime: Date;
  vehicle: IDropdownOption;
}

export const checkConflictingDrives = ({
  driver,
  drives,
  dropoffDate,
  dropoffTime,
  pickupDate,
  pickupTime,
  vehicle,
}: DriveProps): boolean => {
  const overlappingDrives = drives.filter((drive: IBookingDrive) => {
    const existDriveRange = moment.range(new Date(drive.pickupDate), new Date(drive.dropoffDate));
    const selectedDriveRange = moment.range(
      getFullDate(pickupDate, pickupTime),
      getFullDate(dropoffDate, dropoffTime),
    );

    return existDriveRange.overlaps(selectedDriveRange) && drive.vehicle !== vehicle.value;
  });

  for (const drive of overlappingDrives) {
    if (driver?.value && drive.driver === driver.value) {
      return true;
    }
  }

  return false;
};

export const checkConflictingDrivesOnEdit = (drives: IFormDriveEdit[]): boolean => {
  if (drives.length <= 1) return false;

  const overlappingDrives = drives.map((drive: IFormDriveEdit, idx: number) => {
    const comparedStart = getFullDate(drive.pickupDate, drive.pickupTime);
    const comparedEnd = getFullDate(drive.dropoffDate, drive.dropoffTime);
    const comparedRange = moment.range(comparedStart, comparedEnd);

    return drives
      .filter((_, i: number) => i !== idx)
      .map((d: IFormDriveEdit, i: number) => {
        const start = getFullDate(d.pickupDate, d.pickupTime);
        const end = getFullDate(d.dropoffDate, d.dropoffTime);
        const currentRange = moment.range(start, end);

        if (i === idx && !moment(comparedEnd).isSameOrBefore(moment(start))) return true;

        return comparedRange.overlaps(currentRange);
      });
  });

  return overlappingDrives.flat().includes(true);
};
